import type { ProfileInput, Role } from '@/features/auth/api';
import { apiFetch } from '@/lib/api/http';

/** 理事・講師／サクセス担当（D-075）。受講生は会員管理、事務局は事務局アカウントが別にある */
export interface Staff {
  user_id: string;
  email: string;
  display_name: string | null;
  /** プロフィール（D-081・D-082）。photo_url は短命な閲覧URL */
  photo_url: string | null;
  bio: string | null;
  company: string | null;
  position: string | null;
  phone: string | null;
  contact_email: string | null;
  role: Role;
  is_active: boolean;
  assigned_member_count: number;
  /** 1対1の面談を相手として担当した回数（実施済み / これから。D-180） */
  meeting_held_count: number;
  meeting_planned_count: number;
  last_login_at: string | null;
}

export function fetchStaff(params: { role?: Role; includeInactive?: boolean } = {}): Promise<
  Staff[]
> {
  const query = new URLSearchParams();
  if (params.role) query.set('role', params.role);
  if (params.includeInactive === false) query.set('include_inactive', 'false');
  const search = query.toString();
  return apiFetch<Staff[]>(`/staff${search ? `?${search}` : ''}`);
}

export function setStaffActive(userId: string, isActive: boolean): Promise<void> {
  return apiFetch<void>(`/staff/${userId}/active`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_active: isActive }),
  });
}

/** 事務局による代理編集（本人が編集できない事情に備える / D-081） */
export function updateStaffProfile(userId: string, input: ProfileInput): Promise<Staff> {
  return apiFetch<Staff>(`/staff/${userId}/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function uploadStaffPhoto(userId: string, file: File): Promise<Staff> {
  const body = new FormData();
  body.append('file', file);
  return apiFetch<Staff>(`/staff/${userId}/profile/photo`, { method: 'PUT', body });
}

export function deleteStaffPhoto(userId: string): Promise<Staff> {
  return apiFetch<Staff>(`/staff/${userId}/profile/photo`, { method: 'DELETE' });
}

/**
 * パスワードを紛失した担当者の救済（D-082）。
 * 事務局が本人のパスワードを知らずに済む「再設定メール」を基本とする。
 */
export function sendStaffPasswordResetEmail(userId: string): Promise<void> {
  return apiFetch<void>(`/staff/${userId}/password-reset-email`, { method: 'POST' });
}

/** メールが届かない場合の最後の手段。平文はこの応答にしか現れない */
export function issueStaffTemporaryPassword(
  userId: string,
): Promise<{ temporary_password: string }> {
  return apiFetch<{ temporary_password: string }>(`/staff/${userId}/temporary-password`, {
    method: 'POST',
  });
}

/** 選択欄に出す表示名（同姓同名や未設定に備えてメールも添える） */
export function staffLabel(staff: Staff): string {
  return `${staff.display_name ?? staff.email}（${staff.email}）`;
}
